import { useTranslation } from 'react-i18next';
import type {
  UserRole,
  DriverProfileResponse,
  FleetManagerProfileResponse,
  ShipperProfileResponse,
} from '../../api/types';
import { ProfileMediaPreview } from './ProfileMediaPreview';
import styles from './ProfileDocuments.module.css';

export function ProfileDocuments({
  userId,
  profile,
  avatarUrl,
  autoLoad = false,
  role,
  onEditDocument,
  documentLabels,
  embedded = false,
}: {
  userId: number;
  documentLabels?: readonly string[];
  embedded?: boolean;
  role?: UserRole;
  onEditDocument?: (focusId: string) => void;
  avatarUrl?: string | null;
  autoLoad?: boolean;
  profile: DriverProfileResponse | FleetManagerProfileResponse | ShipperProfileResponse | null;
}) {
  const { t } = useTranslation();
  const documents: [string, string | null | undefined][] = [
    ['Profile photo', avatarUrl],
    ['DL', profile && 'dlDocumentUrl' in profile ? profile.dlDocumentUrl : null],
    ['RC', profile && 'rcDocumentUrl' in profile ? profile.rcDocumentUrl : null],
    ['PAN', profile && 'panDocumentUrl' in profile ? profile.panDocumentUrl : null],
    ['GST', profile && 'gstDocumentUrl' in profile ? profile.gstDocumentUrl : null],
    ['Bank proof', profile && 'bankProofUrl' in profile ? profile.bankProofUrl : null],
  ];
  const expected =
    role === 'DRIVER'
      ? ['DL', 'RC']
      : role === 'FLEET_MANAGER'
        ? ['PAN', 'GST', 'Bank proof']
        : role === 'SHIPPER'
          ? ['PAN', 'GST']
          : [];
  const focus = (label: string) => `document-${label === 'Bank proof' ? 'BANK_PROOF' : label}`;
  return (
    <section
      aria-label={t('domain.profileDocuments.sectionLabel')}
      className={embedded ? styles.embeddedDocuments : styles.documents}
    >
      {!embedded && <h3>{t('domain.profileDocuments.heading')}</h3>}
      <div className={styles.grid}>
        {documents
          .filter(
            ([label, url]) =>
              (!documentLabels || documentLabels.includes(label)) &&
              (!!url || expected.includes(label)),
          )
          .map(([label, url]) => (
            <div key={`${userId}:${label}:${url ?? 'missing'}`}>
              {url ? (
                <ProfileMediaPreview userId={userId} url={url} label={label} autoLoad={autoLoad} />
              ) : (
                <article className={styles.card} aria-label={`${label} document`}>
                  <h4>{label}</h4>
                  <div className={`${styles.preview} ${styles.placeholder}`}>
                    {t('domain.profileDocuments.notUploaded', { label })}
                  </div>
                </article>
              )}
              {onEditDocument && label !== 'Profile photo' && (
                <button
                  type="button"
                  className={styles.documentEdit}
                  onClick={() => onEditDocument(focus(label))}
                >
                  {url ? 'Replace' : 'Upload'} {label}
                </button>
              )}
            </div>
          ))}
      </div>
      {!expected.length && !documents.some(([, url]) => !!url) && (
        <p>{t('domain.profileDocuments.noDocuments')}</p>
      )}
    </section>
  );
}
